import { Form } from '@ant-design/compatible';
import { Alert, Button, Checkbox, Divider, Input } from 'antd';
import { withFormik } from 'formik';
import React from 'react';
import { Prompt } from 'react-router-dom';
import { StringParam, useQueryParams } from 'use-query-params';

import { EnumSelect } from '../../../../components/EnumSelect';
import { PhoneNumberField } from '../../../../components/PhoneNumberField';
import { TimetableField } from '../../../../components/TimetableField';
import { FeatureFlagKey_enum, PermissionKey_enum } from '../../../../graphql/hasura/generated';
import {
  FORM_ITEM_DEFAULT_LAYOUT,
  getHelpText,
  getValidateStatus,
} from '../../../../pages/ModelForm/form-fields';
import { authentication } from '../../../../stores';
import { formatTimestamp } from '../../../../utils';

import {
  handleSubmit,
  IPropertyVirtualDirectoryFormProps,
  IPropertyVirtualDirectoryFormValues,
  mapPropsToValues,
  validate,
} from './formik';

export const PropertyVirtualDirectoryForm = withFormik<
  IPropertyVirtualDirectoryFormProps,
  IPropertyVirtualDirectoryFormValues
>({
  mapPropsToValues,
  validate,
  handleSubmit,
  enableReinitialize: true,
  validateOnChange: false,
  validateOnBlur: false,
})((props) => {
  const { property, values, setFieldValue, touched, errors, isSubmitting, submitCount, dirty } = props;
  const disabled = isSubmitting || !authentication.hasPermission(PermissionKey_enum.Property_Update);
  const [query] = useQueryParams({ tab: StringParam });

  const getFormItemProps = (fieldName: keyof IPropertyVirtualDirectoryFormValues) => ({
    ...FORM_ITEM_DEFAULT_LAYOUT,
    validateStatus: getValidateStatus(errors, fieldName),
    help: getHelpText(errors, fieldName),
  });

  const smartSignsEnabled = property.featureFlags.includes(FeatureFlagKey_enum.SMART_SIGNS);

  return (
    <Form onSubmit={props.handleSubmit} style={{ paddingTop: 20 }}>
      <Form.Item label='Published' {...getFormItemProps('published')}>
        <Checkbox
          checked={values.published}
          onChange={e => setFieldValue('published', e.target.checked)}
          disabled={disabled}
        />
        <p className='help-text'>Display the Virtual Directory for public access.</p>
        {!smartSignsEnabled && values.published && (
          <Alert
            showIcon
            type='warning'
            message='The Virtual Directory will not be accessible until Smart Signs are enabled'
            style={{ marginBottom: 10 }}
          />
        )}
      </Form.Item>
      <Form.Item label='Resident Display Name Format' {...getFormItemProps('userDisplayNameFormat')} required>
        <EnumSelect
          enumTable='VirtualDirectoryUserDisplayNameFormat'
          value={values.userDisplayNameFormat}
          onChange={value => setFieldValue('userDisplayNameFormat', value)}
          disabled={disabled}
          style={{ width: '100%' }}
        />
      </Form.Item>
      <Form.Item label='Leasing Office Phone Number' {...getFormItemProps('leasingOfficePhoneNumber')}>
        <PhoneNumberField
          value={values.leasingOfficePhoneNumber}
          onChange={(value) => setFieldValue('leasingOfficePhoneNumber', value, true)}
          disabled={disabled}
        />
        <p className='help-text'>
          Assign a phone number to receive calls from the Virtual Directory.
        </p>
      </Form.Item>
      <Form.Item label='Leasing Office Display Name' {...getFormItemProps('leasingOfficeDisplayName')}>
        <Input
          name='leasingOfficeDisplayName'
          value={values.leasingOfficeDisplayName || ''}
          onChange={e => setFieldValue('leasingOfficeDisplayName', e.target.value)}
          disabled={disabled}
          type='text'
          maxLength={50}
        />
        <p className='help-text'>Customize the display name for the Leasing Office in the Virtual Directory.</p>
      </Form.Item>
      <Form.Item label='Leasing Office Hours' {...getFormItemProps('leasingOfficeHours')}>
        <TimetableField
          values={values}
          fieldName='leasingOfficeHours'
          modelName='PropertyVirtualDirectory'
          onChange={value => setFieldValue('leasingOfficeHours', value, true)}
          errors={errors}
          touched={touched}
          disabled={disabled}
        />
        <p className='help-text'>
          Set available hours for the Leasing Office to receive calls from the Virtual Directory.
        </p>
      </Form.Item>
      <Form.Item label='Updated At' {...getFormItemProps('updatedAt')}>
        <Input
          name='updatedAt'
          value={formatTimestamp(values.updatedAt)}
          type='text'
          disabled
          style={{ marginBottom: 10 }}
        />
      </Form.Item>
      <Divider />
      <section className='form-submit-section'>
        <Button
          htmlType='submit'
          type='primary'
          size='middle'
          loading={isSubmitting}
          disabled={disabled}
        >
          {isSubmitting ? 'Saving...' : 'Save'}
        </Button>
      </section>
      <Prompt
        when={submitCount === 0 && dirty}
        message={(location) => {
          if (
            (!query.tab || query.tab === 'SETTINGS') && // Ensure the other VD tabs aren't selected
            !location.search.includes('SETTINGS')
          ) {
            return 'You have unsaved changes. Are you sure you want to leave?';
          }

          return true;
        }}
      />
    </Form>
  );
});
